import { LitElement, html } from 'lit'

/**
 * Modal com instruções de instalação do PWA para dispositivos iOS.
 *
 * O Safari/iOS não suporta o evento `beforeinstallprompt`, portanto este
 * componente exibe instruções visuais passo a passo para guiar o usuário
 * na instalação do PWA via "Adicionar à Tela de Início".
 *
 * Exibe exatamente 2 passos:
 * 1. Tocar no ícone de compartilhamento do Safari (quadrado com seta para cima)
 * 2. Rolar e tocar em "Adicionar à Tela de Início"
 *
 * Gerencia dismissão via localStorage para não exibir repetidamente.
 * Resiliente a localStorage indisponível (modo privado Safari).
 *
 * @element install-prompt-ios
 * @fires modal-closed - Quando o modal é fechado pelo usuário
 */
export class InstallPromptIOS extends LitElement {
  static properties = {
    open: { type: Boolean, reflect: true }
  }

  /** Chave do localStorage para persistir dismissão do prompt de instalação */
  static DISMISS_KEY = 'equilibra-ios-install-dismissed'

  /**
   * Desabilita Shadow DOM para utilizar Bulma CSS global e tokens do Design System.
   * Segue o padrão dos demais modais do projeto (modal-alert, modal-confirm, etc).
   * @returns {this} O próprio elemento como render root
   */
  createRenderRoot () {
    return this
  }

  constructor () {
    super()
    this.open = false
  }

  /**
   * Verifica se o prompt de instalação já foi dismissido pelo usuário.
   *
   * Consulta o localStorage pela chave DISMISS_KEY. Resiliente a
   * localStorage indisponível (modo privado Safari ou bloqueio),
   * retornando `false` como fallback seguro (exibe o prompt normalmente).
   *
   * @returns {boolean} true se o prompt já foi dismissido, false caso contrário
   */
  isDismissed () {
    try {
      return localStorage.getItem(InstallPromptIOS.DISMISS_KEY) === 'true'
    } catch {
      return false
    }
  }

  /**
   * Fecha o modal e persiste a dismissão no localStorage.
   *
   * Seta `open = false` para ocultar o modal, tenta salvar a dismissão
   * no localStorage para não exibir novamente, e dispara o evento
   * `modal-closed` para notificar componentes pais.
   *
   * Se localStorage estiver indisponível (modo privado), fecha o modal
   * normalmente sem persistir — o prompt será exibido novamente no
   * próximo acesso.
   */
  close () {
    if (!this.open) return

    this.open = false

    try {
      localStorage.setItem(InstallPromptIOS.DISMISS_KEY, 'true')
    } catch {
      // localStorage indisponível (modo privado Safari) — fecha sem persistir
    }

    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }))
  }

  render () {
    if (!this.open) return html``

    return html`
      <div
        class="modal is-active"
        role="dialog"
        aria-labelledby="install-prompt-ios-title"
        data-cy="modal-install-ios"
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          z-index: 30000;
          display: flex;
          align-items: center;
          justify-content: center;
        "
      >
        <div
          class="modal-card"
          @click="${e => e.stopPropagation()}"
          style="
            max-width: 400px;
            width: 90%;
            min-height: auto;
            background-color: var(--bg-surface, #151C30);
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
            animation: modalIn 0.2s ease-out;
          "
        >
          <header
            class="modal-card-head"
            style="
              background-color: var(--bg-surface, #151C30);
              border-bottom: 1px solid var(--border, #26304A);
              display: flex;
              align-items: center;
              justify-content: space-between;
            "
          >
            <p
              id="install-prompt-ios-title"
              class="modal-card-title"
              style="color: var(--text-primary, #F4F7FF); margin: 0;"
              data-cy="titulo-modal-install-ios"
            >
              Instalar EquiLibra
            </p>
            <button
              class="delete"
              aria-label="Fechar instruções de instalação"
              data-cy="btn-fechar-modal-install-ios"
              @click="${this.close}"
              style="
                min-width: 44px;
                min-height: 44px;
                width: 44px;
                height: 44px;
              "
            ></button>
          </header>

          <section
            class="modal-card-body"
            style="
              padding: 1.5rem;
              color: var(--text-primary, #F4F7FF);
              background-color: var(--bg-surface, #151C30);
            "
          >
            <p style="color: var(--text-secondary, #A8B3CF); margin-bottom: 1.25rem;">
              Para instalar o app na tela inicial do seu dispositivo, siga os passos abaixo:
            </p>

            <!-- Passo 1: Ícone de compartilhamento -->
            <div
              class="step"
              data-cy="passo-1-compartilhar"
              style="
                display: flex;
                align-items: center;
                gap: 1rem;
                margin-bottom: 1.25rem;
                padding: 1rem;
                background-color: var(--bg-surface-secondary, #1D2740);
                border-radius: var(--radius-input, 12px);
                border: 1px solid var(--border, #26304A);
              "
            >
              <span
                style="
                  font-size: 1.5rem;
                  min-width: 2rem;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: var(--primary, #6120D8);
                  font-weight: bold;
                "
              >1</span>
              <div style="flex: 1;">
                <p style="margin: 0; color: var(--text-primary, #F4F7FF);">
                  Toque no ícone de compartilhamento
                  <svg
                    aria-label="Ícone de compartilhamento do Safari: quadrado com seta para cima"
                    role="img"
                    data-cy="icone-compartilhar-safari"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    style="vertical-align: middle; margin-left: 0.25rem; color: var(--primary, #6120D8);"
                  >
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                    <polyline points="16 6 12 2 8 6"></polyline>
                    <line x1="12" y1="2" x2="12" y2="15"></line>
                  </svg>
                  na barra do Safari
                </p>
              </div>
            </div>

            <!-- Passo 2: Adicionar à Tela de Início -->
            <div
              class="step"
              data-cy="passo-2-adicionar-tela"
              style="
                display: flex;
                align-items: center;
                gap: 1rem;
                padding: 1rem;
                background-color: var(--bg-surface-secondary, #1D2740);
                border-radius: var(--radius-input, 12px);
                border: 1px solid var(--border, #26304A);
              "
            >
              <span
                style="
                  font-size: 1.5rem;
                  min-width: 2rem;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: var(--primary, #6120D8);
                  font-weight: bold;
                "
              >2</span>
              <div style="flex: 1;">
                <p style="margin: 0; color: var(--text-primary, #F4F7FF);">
                  Rolar e tocar em <strong style="color: var(--secondary, #11C5B8);">"Adicionar à Tela de Início"</strong>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <style>
        @keyframes modalIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      </style>
    `
  }

  /**
   * Trata clique no backdrop (fundo escuro) do modal.
   * Fecha o modal quando o usuário clica fora do conteúdo.
   * @private
   */
  _handleBackdropClick () {
    this.close()
  }
}

customElements.define('install-prompt-ios', InstallPromptIOS)
